import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from '@env/environment';
import { StorageHelper } from '@helpers/storage.helper';
import { AccountUserService } from './account-user.service';
import { CurrentUserRolesService } from './current-user-roles.service';

@Injectable({ providedIn: 'root' })
export class SessionNotificationService {
  private connection?: HubConnection;
  private starting?: Promise<void>;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private enabled = false;
  private sessionChecked = false;
  private readonly revokedSubject = new Subject<void>();
  readonly userRevoked$ = this.revokedSubject.asObservable();

  constructor(
    private accountUserService: AccountUserService,
    private currentUserRoles: CurrentUserRolesService
  ) {}

  /** Checks app membership once when the application starts with a saved token. */
  checkCurrentSession(): void {
    if (this.sessionChecked || !StorageHelper.getToken()?.accessToken) return;
    this.sessionChecked = true;

    this.accountUserService.getCurrentUserRoles().subscribe({
      next: response => {
        const result = response as { succeeded?: boolean; data?: unknown };
        const roles = Array.isArray(response) ? response : result?.data;
        if (result?.succeeded === true && Array.isArray(roles) && roles.every(role => typeof role === 'string')) {
          this.currentUserRoles.setRoles(roles);
          if (roles.length === 0) this.revokedSubject.next();
        }
      },
      // A temporary API/network failure is not proof that the account was revoked.
      error: error => console.warn('Could not validate the current app session.', error)
    });
  }

  connect(): void {
    this.enabled = true;
    if (this.connection || this.starting) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }

    const apiRoot = environment.apiBaseUrl.replace(/\/api\/v\d+\/?$/, '');
    
    this.connection = new HubConnectionBuilder()
      .withUrl(`${apiRoot}/hubs/session`, {
        accessTokenFactory: () => StorageHelper.getToken()?.accessToken ?? ''
      })
      .withAutomaticReconnect()
      .build();

    this.connection.on('user-revoked', () => this.revokedSubject.next());
    const connection = this.connection;
    connection.onclose(error => {
      if (this.connection === connection) this.connection = undefined;
      if (error) console.warn('Session notification connection closed.', error);
      this.scheduleReconnect();
    });
    this.starting = connection.start()
      .catch(error => {
        console.warn('Could not connect to session notifications.', error);
        if (this.connection === connection) this.connection = undefined;
        this.scheduleReconnect();
      })
      .finally(() => { this.starting = undefined; });
  }

  private scheduleReconnect(): void {
    if (!this.enabled || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, 5000);
  }

  async disconnect(): Promise<void> {
    this.enabled = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    const connection = this.connection;
    this.connection = undefined;
    this.starting = undefined;
    if (connection) await connection.stop();
  }
}
